import { useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Bubble } from "@/components/diagrams/Bubble";
import { SmokeCanvas } from "@/components/fx/SmokeCanvas";
import { Button } from "@/components/ui/button";
import { WORKSHOP_PANEL_DATA } from "./workshop-panel-data";

/**
 * WS8 — Prepare / Flight Plan. The vanilla `MC_WS8` panel
 * (`_legacy/mc.html` lines 1157-1327) is the most ornate of the eight:
 *
 *   - Four phase bubbles in a single row at cy=149 (PHASE 0 / 1 / 2+
 *     / BEYOND), with sub-labels under each.
 *   - A curved Bézier flight path from lower-left (42, 76) to upper-
 *     right (660, -77), stroked with a blue → melon → green linear
 *     gradient.
 *   - A rocket that orbits the path via `<animateMotion>` on an 11s
 *     loop, with a 3.3s negative offset so it boots mid-flight.
 *   - Soft "rocket-pass" glow rings on each phase bubble, lit by JS
 *     when the rocket is within 90 x-units. Last bubble (BEYOND)
 *     glows green instead of melon.
 *   - Small arrow markers + dots between bubbles to suggest movement.
 *
 * The rocket animation itself is declarative `<animateMotion>` —
 * native SVG, no JS state. The glow-pass synchronisation is the only
 * piece that needs an RAF loop, ported from `_legacy/xaes-fx.js`
 * lines 91-130 with the same StrictMode-safe pattern the FX canvases
 * use (rafId stored in a ref + `disposed` flag the loop checks
 * before scheduling the next frame).
 */

const ROCKET_DUR_MS = 11_000;
// Negative `begin` value in the vanilla animateMotion (3.3s) — start
// the rocket mid-flight so the first frame doesn't show it parked.
const ROCKET_OFFSET_MS = 3_300;
const PASS_THRESHOLD = 90;

const GLOW_CONFIGS = [
  { cx: 80, strokeVar: "var(--accent-melon)", rgbVar: "var(--accent-melon-rgb)" },
  { cx: 250, strokeVar: "var(--accent-melon)", rgbVar: "var(--accent-melon-rgb)" },
  { cx: 420, strokeVar: "var(--accent-melon)", rgbVar: "var(--accent-melon-rgb)" },
  { cx: 590, strokeVar: "var(--accent-green)", rgbVar: "var(--accent-green-rgb)" },
] as const;

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

export function Ws8Panel() {
  const data = WORKSHOP_PANEL_DATA.WS8;
  const navigate = useNavigate();
  const pathRef = useRef<SVGPathElement | null>(null);
  const glowRefs = useRef<Array<SVGCircleElement | null>>([null, null, null, null]);

  useEffect(() => {
    const start = performance.now() - ROCKET_OFFSET_MS;
    const litState = GLOW_CONFIGS.map(() => false);
    let rafId: number | null = null;
    let disposed = false;

    function tick(now: number) {
      if (disposed) return;
      const path = pathRef.current;
      if (!path) {
        rafId = requestAnimationFrame(tick);
        return;
      }
      const elapsed = (now - start) % ROCKET_DUR_MS;
      const frac = easeInOut(elapsed / ROCKET_DUR_MS);
      const len = path.getTotalLength();
      const pt = path.getPointAtLength(frac * len);

      GLOW_CONFIGS.forEach((g, i) => {
        const el = glowRefs.current[i];
        if (!el) return;
        const isNear = Math.abs(pt.x - g.cx) < PASS_THRESHOLD;
        if (isNear !== litState[i]) {
          litState[i] = isNear;
          el.style.opacity = isNear ? "1" : "0";
        }
      });
      rafId = requestAnimationFrame(tick);
    }
    rafId = requestAnimationFrame(tick);

    return () => {
      disposed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div
      className="bg-card/70 relative h-full w-full overflow-hidden rounded-md border"
      style={{ borderColor: "color-mix(in srgb, var(--accent-melon) 30%, transparent)" }}
    >
      <SmokeCanvas
        className="pointer-events-none absolute inset-0 z-0 h-full w-full"
        cssVarName="--accent-melon"
      />

      <div className="relative z-[1] flex h-full w-full flex-col">
        {/* Top-centred wordmark (vanilla mc.html lines 1186-1190) */}
        {data.wordmark && (
          <header className="pointer-events-none flex flex-col items-center gap-1 px-4 pt-4 pb-2 text-center">
            <span
              className="text-melon font-sans text-xs font-bold tracking-[0.25em] uppercase"
              style={{
                textShadow:
                  "0 0 6px hsl(var(--accent-melon-hsl) / 0.9), 0 0 16px hsl(var(--accent-melon-hsl) / 0.6), 0 0 32px hsl(var(--accent-melon-hsl) / 0.3)",
              }}
            >
              {data.wordmark.eyebrow}
            </span>
            <span
              className="text-foreground font-sans text-lg font-bold whitespace-nowrap uppercase"
              style={{
                letterSpacing: "0.3em",
                textShadow:
                  "0 0 6px rgba(255,248,236,0.95), 0 0 16px rgba(255,248,236,0.7), 0 0 40px rgba(255,248,236,0.4)",
              }}
            >
              {data.wordmark.lines.join(" ")}
            </span>
            <span className="text-muted-foreground text-xs tracking-wider">
              From Phase 0 to Phase 1 &amp; Beyond.
            </span>
          </header>
        )}

        {/* Flight path + bubble row */}
        <svg
          // Vanilla `0 0 680 265`. We keep `overflow: visible` on this
          // SVG so the rocket can fly past the top edge as in vanilla;
          // the outer panel still clips via its `overflow-hidden`.
          viewBox="0 0 680 265"
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          className="block min-h-0 flex-1"
          style={{ overflow: "visible" }}
          role="img"
          aria-label="WS8 — Flight Plan: Phase 0 to Beyond"
        >
          <defs>
            <radialGradient id="grad-WS8" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="hsl(var(--accent-melon-hsl) / 0.13)" />
              <stop offset="100%" stopColor="hsl(var(--accent-melon-hsl) / 0.02)" />
            </radialGradient>
            {/* BEYOND bubble fill — green to match its stroke. */}
            <radialGradient id="grad-WS8-green" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="hsl(var(--accent-green-hsl) / 0.13)" />
              <stop offset="100%" stopColor="hsl(var(--accent-green-hsl) / 0.02)" />
            </radialGradient>
            <filter id="glow-WS8" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <linearGradient
              id="flightPathGradWS8"
              x1="40"
              y1="75"
              x2="660"
              y2="-77"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="var(--accent-true-blue)" />
              <stop offset="55%" stopColor="var(--accent-melon)" />
              <stop offset="100%" stopColor="var(--accent-green)" />
            </linearGradient>
            <filter id="rocketGlowWS8" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="2.2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="ws8PassGlow" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="7" result="blur1" />
              <feGaussianBlur stdDeviation="3.5" result="blur2" in="SourceGraphic" />
              <feMerge>
                <feMergeNode in="blur1" />
                <feMergeNode in="blur2" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <marker
              id="arrowWS8"
              markerWidth="6"
              markerHeight="6"
              refX="3"
              refY="3"
              orient="auto"
            >
              <path d="M0,1 L5,3 L0,5 Z" fill="var(--accent-melon)" fillOpacity={0.4} />
            </marker>
          </defs>

          {/* Animated flight path with rocket */}
          <g pointerEvents="none">
            {/* The motion-only path — invisible but referenced by the
                rocket's <animateMotion mpath>. */}
            <path
              ref={pathRef}
              id="ws8-flight-path-motion"
              d="M 42 76 C 150 58 246 66 340 64 C 420 64 455 63 480 57 C 530 45 553 15 580 -17 C 607 -50 631 -67 660 -77"
              fill="none"
              stroke="none"
            />
            {/* Soft underglow stroke */}
            <path
              d="M 42 76 C 150 58 246 66 340 64 C 420 64 455 63 480 57 C 530 45 553 15 580 -17 C 607 -50 631 -67 660 -77"
              fill="none"
              stroke="url(#flightPathGradWS8)"
              strokeWidth={8}
              strokeLinecap="round"
              opacity={0.13}
              filter="url(#glow-WS8)"
            />
            {/* Crisp core stroke — marching dashes flow forward
                (toward the destination) with the path's gradient.
                Underglow stays solid so the blur doesn't strobe. */}
            <path
              d="M 42 76 C 150 58 246 66 340 64 C 420 64 455 63 480 57 C 530 45 553 15 580 -17 C 607 -50 631 -67 660 -77"
              fill="none"
              stroke="url(#flightPathGradWS8)"
              strokeWidth={2.2}
              strokeLinecap="round"
              strokeDasharray="8 6"
              opacity={0.9}
              filter="url(#glow-WS8)"
            >
              <animate
                attributeName="stroke-dashoffset"
                from="0"
                to="-14"
                dur="1.2s"
                repeatCount="indefinite"
              />
            </path>
            {/* Rocket — declarative <animateMotion> on the path */}
            <g filter="url(#rocketGlowWS8)">
              <animateMotion
                dur="11s"
                begin="-3.3s"
                repeatCount="indefinite"
                rotate="auto"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.42 0 0.58 1"
              >
                <mpath href="#ws8-flight-path-motion" />
              </animateMotion>
              <g transform="scale(0.85)">
                {/* Body + nose cone */}
                <path
                  d="M 15 0 C 14 -5 8 -7 1 -7 L -9 -5 L -9 5 L 1 7 C 8 7 14 5 15 0 Z"
                  fill="#FFF8EC"
                  stroke="var(--accent-melon)"
                  strokeWidth={1.4}
                  strokeLinejoin="round"
                />
                {/* Top fin */}
                <path
                  d="M -5 -7 L -12 -13 L -9 -5"
                  fill="var(--accent-true-blue)"
                  opacity={0.75}
                  stroke="var(--accent-true-blue)"
                  strokeWidth={1}
                  strokeLinejoin="round"
                />
                {/* Bottom fin */}
                <path
                  d="M -5 7 L -12 13 L -9 5"
                  fill="var(--accent-true-blue)"
                  opacity={0.75}
                  stroke="var(--accent-true-blue)"
                  strokeWidth={1}
                  strokeLinejoin="round"
                />
                {/* Porthole */}
                <circle
                  cx={5}
                  cy={0}
                  r={2.5}
                  fill="hsl(var(--card))"
                  stroke="var(--accent-true-blue)"
                  strokeWidth={1.1}
                />
                {/* Flame — opacity cycles for the flicker effect */}
                <path
                  d="M -9 -4 C -14 -3 -20 -2 -24 0 C -20 2 -14 3 -9 4 Z"
                  fill="var(--accent-gold)"
                  opacity={0.85}
                >
                  <animate
                    attributeName="opacity"
                    values="0.45;1;0.55;0.9;0.45"
                    dur="0.75s"
                    repeatCount="indefinite"
                  />
                </path>
              </g>
            </g>
          </g>

          {/* Connectors between phase bubbles — small dashed arrows */}
          <line
            x1={145}
            y1={149}
            x2={185}
            y2={149}
            stroke="var(--accent-melon)"
            strokeOpacity={0.4}
            strokeWidth={1.5}
            strokeDasharray="3,2"
            markerEnd="url(#arrowWS8)"
          />
          <line
            x1={315}
            y1={149}
            x2={355}
            y2={149}
            stroke="var(--accent-melon)"
            strokeOpacity={0.4}
            strokeWidth={1.5}
            strokeDasharray="3,2"
            markerEnd="url(#arrowWS8)"
          />
          <line
            x1={485}
            y1={149}
            x2={525}
            y2={149}
            stroke="var(--accent-melon)"
            strokeOpacity={0.4}
            strokeWidth={1.5}
            strokeDasharray="3,2"
            markerEnd="url(#arrowWS8)"
          />

          {/* Accent dots between connector arrows */}
          <circle
            cx={165}
            cy={149}
            r={1.5}
            fill="var(--accent-melon)"
            opacity={0.4}
            filter="url(#glow-WS8)"
          />
          <circle
            cx={335}
            cy={149}
            r={1.5}
            fill="var(--accent-melon)"
            opacity={0.4}
            filter="url(#glow-WS8)"
          />
          <circle
            cx={505}
            cy={149}
            r={1.5}
            fill="var(--accent-melon)"
            opacity={0.4}
            filter="url(#glow-WS8)"
          />

          {/* Phase bubbles */}
          {data.bubbles.map((config) => (
            <Bubble key={config.id} config={config} />
          ))}

          {/* Rocket-pass glow rings — lit by the RAF tracker above */}
          <g pointerEvents="none">
            {GLOW_CONFIGS.map((g, i) => (
              <circle
                key={i}
                ref={(el) => {
                  glowRefs.current[i] = el;
                }}
                cx={g.cx}
                cy={149}
                r={70}
                fill={`rgba(${g.rgbVar}, 0.05)`}
                stroke={g.strokeVar}
                strokeWidth={2}
                style={{
                  opacity: 0,
                  transition: "opacity 0.35s ease-out",
                }}
                filter="url(#ws8PassGlow)"
              />
            ))}
          </g>
        </svg>
      </div>

      <Button
        size="sm"
        onClick={() => navigate(`/workshop/${data.workshopId}`)}
        className="bg-melon text-primary-foreground absolute right-3 bottom-3 z-10 hover:opacity-90"
      >
        Enter workshop
        <ArrowRight className="ml-1.5 h-3.5 w-3.5" strokeWidth={2} aria-hidden />
      </Button>
    </div>
  );
}
